/**
 * @module lib/TimeUtils
 */

import get from 'lodash/get';

import * as Constants from '../constants';
import { type ValueOf } from '../types';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
export const MILLISECS_SECS = 1000;
export const MILLISECS_MINUTE = MILLISECS_SECS * 60;
export const MILLISECS_HOUR = MILLISECS_MINUTE * 60;
export const MILLISECS_DAY = MILLISECS_HOUR * 24;
export const MILLISECS_WEEK = MILLISECS_DAY * 7;
export const MILLISECS_MONTH = MILLISECS_DAY * 30; // Assumes that all months have 30 days

/* Startup Performance Utils */
export function recordTimeAtMoment(momentName: ValueOf<typeof Constants.App.TimingMoments>): void {
  window.performance.measure(momentName);
}

export function recordTimeAtMomentByMarkers(
  momentName: string,
  startMark: string,
  endMark: string,
): void {
  if (momentName) {
    if (startMark && endMark) {
      window.performance.measure(momentName, startMark, endMark);
    } else if (startMark && !endMark) {
      window.performance.measure(momentName, startMark);
    } else if (!startMark && endMark) {
      window.performance.measure(momentName, undefined, endMark);
    }
  }
}

export function markTimeAtMoment(markName: string): void {
  window.performance.mark(markName);
}

export function doesMarkExist(markName: string): boolean {
  return window.performance.getEntriesByName(markName).length > 0;
}

export function getMarkByName(markName: string): PerformanceEntry | undefined {
  const entries = window.performance.getEntriesByType('mark') || [];
  return entries.find(entry => get(entry, 'name', null) === markName);
}

export function clearMarkByName(name: string): void {
  window.performance.clearMarks(name);
}

export function clearMeasureByName(name: string): void {
  window.performance.clearMeasures(name);
}

function getApiLoadTimeMeasurements(): number {
  const ApiMeasurements = Constants.App.ApiMeasurements;
  const ApiCheckpointMarks = Constants.App.ApiCheckpointMarks;
  const apisToMeasure = [
    {
      markName: ApiMeasurements.FETCH_STATIONS_CALL_DURATION,
      startMark: ApiCheckpointMarks.FETCH_STATIONS_REQUEST,
      endMark: ApiCheckpointMarks.FETCH_STATIONS_RESPONSE,
    },
    {
      markName: ApiMeasurements.COLLECTIONS_GET_ITEMS_CALL_DURATION,
      startMark: ApiCheckpointMarks.COLLECTIONS_GET_ITEMS_REQUEST,
      endMark: ApiCheckpointMarks.COLLECTIONS_GET_ITEMS_RESPONSE,
    },
    {
      markName: ApiMeasurements.COLLECTIONS_GET_SORTED_PLAYLISTS_CALL_DURATION,
      startMark: ApiCheckpointMarks.COLLECTIONS_GET_SORTED_PLAYLISTS_REQUEST,
      endMark: ApiCheckpointMarks.COLLECTIONS_GET_SORTED_PLAYLISTS_RESPONSE,
    },
  ];

  const results = apisToMeasure
    .filter(({ endMark }) => !!window.performance.getEntriesByName(endMark).length)
    .reduce(
      (times, { markName, startMark, endMark }) => {
        window.performance.measure(markName, startMark, endMark);
        const startTime = get(window.performance.getEntriesByName(markName), '[0].startTime', null);
        const duration = get(window.performance.getEntriesByName(markName), '[0].duration', null);
        const endTime = startTime + duration;
        // @ts-expect-error TS(2345) FIXME: Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
        times.startTimes.push(startTime);
        // @ts-expect-error TS(2345) FIXME: Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
        times.endTimes.push(endTime);
        return times;
      },
      { startTimes: [], endTimes: [] },
    );

  const apiStart = Math.min(...results.startTimes);
  const apiEnd = Math.max(...results.endTimes);

  return apiEnd - apiStart;
}

export function getTimingMeasurements(): Record<string, string> {
  return {
    [Constants.App.TimingMetrics.TIME_TO_START_APP]: `${get(
      window.performance.getEntriesByName(Constants.App.TimingMoments.START_APP),
      '[0].duration',
      null,
    )}`,
    [Constants.App.TimingMetrics.TIME_TO_INITIALIZE_STORE]: `${get(
      window.performance.getEntriesByName(Constants.App.TimingMoments.STORE_INITIALIZED),
      '[0].duration',
      null,
    )}`,
    [Constants.App.TimingMetrics.TIME_TO_INITIALIZE_APP]: `${get(
      window.performance.getEntriesByName(Constants.App.TimingMoments.APP_INITIALIZED),
      '[0].duration',
      null,
    )}`,
    [Constants.App.TimingMetrics.TIME_TO_FIRST_PAINT]: `${get(
      window.performance.getEntriesByName(Constants.App.TimingMoments.FIRST_PAINT),
      '[0].duration',
      null,
    )}`,
    [Constants.App.TimingMetrics.API_LOAD_TIME]: `${getApiLoadTimeMeasurements()}`,
  };
}

export function getReadyToPlayTimingMeasurement(): Record<string, string> {
  return {
    [Constants.App.TimingMetrics.TIME_TO_READY_TO_PLAY]: `${get(
      window.performance.getEntriesByName(Constants.App.TimingMoments.READY_TO_PLAY),
      '[0].duration',
      null,
    )}`,
  };
}

export function getAudioPlayerTimeToAudioTimingMeasurement(): number {
  const measureName = Constants.App.PlaybackMeasures.AUDIO_PLAYER_TIME_TO_AUDIO_DURATION;
  const startMark = Constants.App.PlaybackMarks.AUDIO_PLAY;
  const endMark = Constants.App.PlaybackMarks.AUDIO_PLAYING;

  window.performance.measure(measureName, startMark, endMark);
  const duration = get(window.performance.getEntriesByName(measureName), '[0].duration', null);

  // After the measurement, clear all related measures and marks
  window.performance.clearMeasures(measureName);
  window.performance.clearMarks(startMark);
  window.performance.clearMarks(endMark);

  return duration;
}

/* Search Performance Utils */
export function measureDurationBetweenMarksForQuery(
  measureName: string,
  startMark: string,
  endMark: string,
  query: string,
): void {
  window.performance.measure(
    `${measureName}:${query}`,
    `${startMark}:${query}`,
    `${endMark}:${query}`,
  );
}

export function markTimeAtMomentForQuery(markName: string, query: string): void {
  window.performance.mark(`${markName}:${query}`);
}

export function getMarksByNameForQuery(markName: string, query: string): PerformanceEntryList {
  return window.performance.getEntriesByName(`${markName}:${query}`);
}

const SearchTriggerMarks = Constants.App.SearchTriggerMarks;
const SearchCheckpointMarks = Constants.App.SearchCheckpointMarks;
const SearchMeasures = Constants.App.SearchMeasures;
const AllSearchMarks = { ...SearchTriggerMarks, ...SearchCheckpointMarks };
export function clearSearchMarksAndMeasures(): void {
  const marksAndMeasures = window.performance.getEntries();
  const markKeys = Object.values(AllSearchMarks);
  const measureKeys = Object.values(SearchMeasures);
  marksAndMeasures.forEach(val => {
    if (new RegExp(markKeys.join('|')).test(val.name)) {
      window.performance.clearMarks(val.name);
    } else if (new RegExp(measureKeys.join('|')).test(val.name)) {
      window.performance.clearMeasures(val.name);
    }
  });
}

export const getSearchMeasurementViewModeForQuery = (query: string): string => {
  return Object.values(SearchTriggerMarks).reduce((acc, val) => {
    if (getMarksByNameForQuery(val, query).length > 0) {
      return val;
    }
    return acc;
  }, '');
};

export const getSearchMeasurementsForQuery = (
  query: string,
): Record<string, string> | undefined => {
  measureDurationBetweenMarksForQuery(
    SearchMeasures.SEARCH_STATS_SOD_CALL_DURATION,
    SearchCheckpointMarks.SEARCH_STATS_SOD_REQUEST,
    SearchCheckpointMarks.SEARCH_STATS_SOD_RESPONSE,
    query,
  );

  const activeMark = getSearchMeasurementViewModeForQuery(query);
  if (activeMark.length === 0) {
    return;
  }

  measureDurationBetweenMarksForQuery(
    SearchMeasures.SEARCH_STATS_TTS_DURATION,
    activeMark,
    SearchCheckpointMarks.SEARCH_STATS_RESULTS_RENDER,
    query,
  );

  const sodCallDurationKey = `${Constants.App.SearchMeasures.SEARCH_STATS_SOD_CALL_DURATION}:${query}`;
  const ttsDurationKey = `${Constants.App.SearchMeasures.SEARCH_STATS_TTS_DURATION}:${query}`;
  return {
    [Constants.App.SearchMeasures.SEARCH_STATS_SOD_CALL_DURATION]: get(
      window.performance.getEntriesByName(sodCallDurationKey),
      '[0].duration',
      null,
    ),
    [Constants.App.SearchMeasures.SEARCH_STATS_TTS_DURATION]:
      get(window.performance.getEntriesByName(ttsDurationKey), '[0].duration', null) +
      Constants.App.SEARCH_POLLING_FREQ_MSEC,
  };
};

/* General Time Utils */

export function getNow(): number {
  return Date.now();
}

const perf = window.performance || Date;
export function perfNow(): number {
  return perf.now();
}

export function millisToSeconds(millis: number): number {
  return Math.floor(millis / 1000);
}

export function secondsToMillis(seconds: number): number {
  return Math.floor(seconds * 1000);
}

/**
 *
 * @param {number} seconds - number of seconds
 * @returns {string} Formatted timestamp as MM:SS or HH:MM:SS
 */
export function formatTime(seconds: number): string {
  const t = isNaN(seconds) || seconds < 0 ? 0 : seconds;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = Math.floor(t % 60);
  const mm = h > 0 ? `0${m}`.slice(-2) : `${m}`;
  const ss = `0${s}`.slice(-2);

  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 *
 * @param {number} timeInSeconds - number of seconds
 * @returns {string} Formatted timestamp as HH:MM:SS.sss
 */
export function formatTimeWithMillis(timeInSeconds: number): string {
  const pad = function (num: string | number, size: number): string {
    return ('000' + num).slice(size * -1);
  };
  const time = parseFloat(timeInSeconds.toString()).toFixed(3);
  // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
  const hours = Math.floor(time / 60 / 60);
  // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
  const minutes = Math.floor(time / 60) % 60;
  // @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
  const seconds = Math.floor(time - minutes * 60);
  const milliseconds = time.slice(-3);

  return pad(hours, 2) + ':' + pad(minutes, 2) + ':' + pad(seconds, 2) + '.' + pad(milliseconds, 3);
}

export function formatMillisToDashDate(millis: number): string {
  const date = new Date(millis);
  return date.toISOString().split('T')[0];
}

export function formatSpeechFriendlyTime(seconds: number): Record<string, number> {
  const t = isNaN(seconds) || seconds < 0 ? 0 : seconds;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = Math.floor((t % 3600) % 60);
  return { h, m, s };
}

/**
 *
 * @param {number} monthNum [0...11]
 * @returns {string}
 */
export function getMonthName(monthNum: number): string {
  return MONTH_NAMES[monthNum];
}

export function isSameDay(now: Date, prev: Date): boolean {
  return now.toDateString() === prev.toDateString();
}

export function hasTimestampExpired(timestamp: number): boolean {
  const currentTimestamp = Math.floor(Date.now());
  return currentTimestamp >= timestamp;
}

export function daysToMilliseconds(days: number): number {
  return days * 24 * 3600 * 1000;
}

export function getSecondsAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_SECS);
}

export function getMinutesAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_MINUTE);
}

export function getHoursAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_HOUR);
}

export function getDaysAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_DAY);
}

export function getWeeksAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_WEEK);
}

export function getMonthsAgo(nowTimestamp: number, prevTimestamp: number): number {
  return Math.round((nowTimestamp - prevTimestamp) / MILLISECS_MONTH);
}

export class timeoutTimer {
  stillGoing: boolean;
  timeout: ReturnType<typeof setTimeout> | null | undefined;
  constructor(ms: number) {
    if (ms) {
      this.stillGoing = true;
      this.timeout = setTimeout(() => {
        this.stillGoing = false;
      }, ms);
    } else {
      this.stillGoing = false;
    }
  }

  cancel(): void {
    this.stillGoing = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  isStillGoing(): boolean {
    return this.stillGoing;
  }
}
